import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, ShoppingCart, CreditCard, Brush } from 'lucide-react';
import Navbar from '../../layout/Navbar';
import Footer from '../../layout/Footer';
import { Link } from 'react-router-dom';

const ECommerce = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll('[data-animate]');

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach(observer => observer.disconnect());
    }, []);

    const features = [
        { name: 'Bespoke Storefronts', desc: 'Unique, brand-aligned designs tailored to your vision.' },
        { name: 'Secure Payment Systems', desc: 'Integration with trusted global payment gateways.' },
        { name: 'Advanced Inventory Tools', desc: 'Real-time tracking with custom automation.' },
        { name: 'SEO Optimization', desc: 'Custom strategies to boost organic traffic.' },
        { name: 'Mobile-First Design', desc: 'Visually stunning stores optimized for all devices.' },
        { name: 'Custom Analytics', desc: 'Tailored dashboards for actionable insights.' },
    ];

    const solutions = [
        { name: "Custom Shopify Stores", desc: "Unique designs with Shopify's robust platform." },
        { name: "WooCommerce Solutions", desc: "Custom WordPress stores with full flexibility." },
        { name: "Bespoke MERN Stores", desc: "Fully custom e-commerce with modern tech stacks." },
        { name: "Custom Marketplaces", desc: "Multi-vendor platforms tailored to your needs." },
        { name: "Headless Commerce", desc: "Custom API-driven solutions for flexibility." },
        { name: "PWA Customization", desc: "Progressive web apps with bespoke designs." },
    ];

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div
                        className="animate-fade-in-up"
                        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                    >
                        <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                            Custom E-Commerce
                            <br />
                            <span className="text-green-500 inline-block animate-pulse">Designs</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
                            Crafting <span className="text-green-500 font-semibold">bespoke e-commerce websites</span> with custom designs that elevate your brand and drive sales.
                        </p>
                    </div>

                    {/* Scroll Indicator */}
                    <div className="animate-bounce mt-16">
                        <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gray-900" data-animate>
                <div
                    className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                        }`}
                >
                    <div className="text-center text-white">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-green-400">
                            OneHexa Tech
                        </h2>
                        <p className="text-lg text-gray-300">
                            OneHexa Tech can design the perfect project tailored to your requirements.
                        </p>
                    </div>
                </div>
            </section>

            {/* Service Overview Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Our Custom E-Commerce <span className="text-green-500">Expertise</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize">
                            We specialize in delivering <span className="text-green-500 font-semibold">custom-designed e-commerce websites</span> tailored to your brand, built with modern technologies for scalability and performance.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Approach */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Brush className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Approach</h3>
                            <p className="text-gray-600 leading-relaxed">
                                We create <span className="text-green-500 font-semibold">unique, brand-centric designs</span> with user-friendly interfaces to maximize engagement and conversions.
                            </p>
                        </div>

                        {/* Benefits */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <CreditCard className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Key Benefits</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Stand out with <span className="text-green-500 font-semibold">custom designs</span>, improve customer retention, and boost sales with tailored solutions.
                            </p>
                        </div>

                        {/* Process */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <ArrowRight className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Process</h3>
                            <ul className="list-disc list-inside text-gray-600 space-y-2">
                                <li>Brand Analysis & Design</li>
                                <li>Custom Development</li>
                                <li>Integration & Testing</li>
                                <li>Launch & Optimization</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-animate>
                <div className={`max-w-7xl mx-auto text-center transition-all duration-1000 delay-250 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                        Core <span className="text-green-500">Features</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
                        Custom features for a <span className="text-green-500 font-semibold">tailored e-commerce experience</span>.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, idx) => (
                            <div key={idx} className="bg-gray-50 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border hover:border-green-300">
                                <ShoppingCart className="w-10 h-10 text-green-500 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.name}</h3>
                                <p className="text-gray-600">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Solutions Section with Show More */}
            <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Custom E-Commerce <span className="text-green-500">Solutions</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Bespoke platforms designed to align with your brand's unique identity.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 cursor-pointer">
                        {(showMore ? solutions : solutions.slice(0, 6)).map((solution, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200"
                            >
                                <h3 className="text-lg font-bold text-gray-600 mb-1 group-hover:text-green-600 transition-colors duration-300">{solution.name}</h3>
                                <p className="text-sm text-gray-600">{solution.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Create Your <span className="text-green-400">Custom Store</span>
                    </h2>
                    <p className="text-xl text-gray-300 mb-8">
                        Partner with OneHexa Tech for uniquely designed e-commerce solutions.
                    </p>
                    <Link to="/contact">
                        <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                            Design Your Store
                        </button>
                    </Link>
                </div>
            </section>

            <Footer />

            <style jsx>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 1s ease-out;
                }
            `}</style>
        </div>
    );
};

export default ECommerce;